export type ComboName =
  | 'five_kind'
  | 'four_kind'
  | 'straight'
  | 'full_house'
  | 'three_kind'
  | 'two_pair'
  | 'one_pair'
  | 'high';

export const COMBO_RANK: Record<ComboName, number> = {
  five_kind: 1,
  four_kind: 2,
  straight: 3,
  full_house: 4,
  three_kind: 5,
  two_pair: 6,
  one_pair: 7,
  high: 8,
};

export interface HandResult {
  combo: ComboName;
  rank: number;
  tiebreakers: number[];
}

const LOW_STRAIGHT = [1, 2, 3, 4, 5];
const HIGH_STRAIGHT = [2, 3, 4, 5, 6];

function sortedDesc(values: number[]): number[] {
  return [...values].sort((a, b) => b - a);
}

function countMap(dice: number[]): Map<number, number> {
  const map = new Map<number, number>();
  for (const d of dice) {
    map.set(d, (map.get(d) ?? 0) + 1);
  }
  return map;
}

function isStraight(sortedUnique: number[]): boolean {
  if (sortedUnique.length !== 5) return false;
  const low = sortedUnique.every((v, i) => v === LOW_STRAIGHT[i]);
  const high = sortedUnique.every((v, i) => v === HIGH_STRAIGHT[i]);
  return low || high;
}

function straightHigh(sortedUnique: number[]): number {
  return sortedUnique[sortedUnique.length - 1];
}

export function diceSum(dice: number[]): number {
  return dice.reduce((sum, d) => sum + d, 0);
}

export function getComboRank(combo: ComboName): number {
  return COMBO_RANK[combo];
}

export function evaluateHand(dice: number[]): HandResult {
  if (dice.length !== 5) {
    throw new Error(`evaluateHand expects 5 dice, got ${dice.length}`);
  }

  const counts = countMap(dice);
  const entries = [...counts.entries()].sort((a, b) => {
    if (b[1] !== a[1]) return b[1] - a[1];
    return b[0] - a[0];
  });

  const sortedUnique = [...counts.keys()].sort((a, b) => a - b);
  const sortedDice = sortedDesc(dice);

  const countsOnly = entries.map(([, c]) => c);

  if (countsOnly[0] === 5) {
    return {
      combo: 'five_kind',
      rank: COMBO_RANK.five_kind,
      tiebreakers: [entries[0][0]],
    };
  }

  if (countsOnly[0] === 4) {
    const quad = entries[0][0];
    const kicker = entries[1][0];
    return {
      combo: 'four_kind',
      rank: COMBO_RANK.four_kind,
      tiebreakers: [quad, kicker],
    };
  }

  if (isStraight(sortedUnique)) {
    return {
      combo: 'straight',
      rank: COMBO_RANK.straight,
      tiebreakers: [straightHigh(sortedUnique)],
    };
  }

  if (countsOnly[0] === 3 && countsOnly[1] === 2) {
    const triple = entries[0][0];
    const pair = entries[1][0];
    return {
      combo: 'full_house',
      rank: COMBO_RANK.full_house,
      tiebreakers: [triple, pair],
    };
  }

  if (countsOnly[0] === 3) {
    const triple = entries[0][0];
    const kickers = sortedDesc(entries.slice(1).map(([v]) => v));
    return {
      combo: 'three_kind',
      rank: COMBO_RANK.three_kind,
      tiebreakers: [triple, ...kickers],
    };
  }

  if (countsOnly[0] === 2 && countsOnly[1] === 2) {
    const pairs = entries.filter(([, c]) => c === 2).map(([v]) => v).sort((a, b) => b - a);
    const kicker = entries.find(([, c]) => c === 1)?.[0] ?? 0;
    return {
      combo: 'two_pair',
      rank: COMBO_RANK.two_pair,
      tiebreakers: [pairs[0], pairs[1], kicker],
    };
  }

  if (countsOnly[0] === 2) {
    const pair = entries[0][0];
    const kickers = sortedDesc(entries.slice(1).map(([v]) => v));
    return {
      combo: 'one_pair',
      rank: COMBO_RANK.one_pair,
      tiebreakers: [pair, ...kickers],
    };
  }

  return {
    combo: 'high',
    rank: COMBO_RANK.high,
    tiebreakers: sortedDice,
  };
}

/** Returns positive if a wins, negative if b wins, 0 if tie */
export function compareHands(a: HandResult, b: HandResult): number {
  if (a.rank !== b.rank) {
    return b.rank - a.rank;
  }

  const len = Math.max(a.tiebreakers.length, b.tiebreakers.length);
  for (let i = 0; i < len; i += 1) {
    const av = a.tiebreakers[i] ?? 0;
    const bv = b.tiebreakers[i] ?? 0;
    if (av !== bv) return av - bv;
  }

  return 0;
}

export function compareDiceHands(diceA: number[], diceB: number[]): number {
  return compareHands(evaluateHand(diceA), evaluateHand(diceB));
}

export function boardToDice(board: (number | null)[]): number[] | null {
  if (board.some((v) => v === null)) return null;
  return board as number[];
}
